<template>
	<div class="piggy-bank-offer-detail template-layout">
		<OfferHeaderBlock
			:contentData="contentData"
			@closePopup="$emit('closePopup', $event)"
		/>
		<OfferRibbonBlock :contentData="contentData" />
		<OfferContentBlock
			:skin="`${contentData.attributes?.skin + (questCompleted ? '--full' : '--progress')}`"
		>
			<template #content>
				<div class="piggy-bank-offer-detail__content">
					<Tooltip customWidth="18rem" :placement="Placements.RIGHT">
						<template #element>
							<AppIconButton :type="IconTypes.INFO" :size="32" />
						</template>
						<template #tooltip>
							<span>
								{{
									questCompleted
										? translate("ui.treasury_full_info")
										: replacePlaceholder(
												translate(
													"ui.fill_treasury_info",
												),
												"{percent}",
												"20%",
											)
								}}
							</span>
						</template>
					</Tooltip>

					<span class="piggy-bank-offer-detail__content-cta">
						{{
							translate(
								questCompleted
									? "ui.treasury_full_cta"
									: "ui.fill_treasury_cta",
							)
						}}
					</span>

					<div class="piggy-bank-offer-detail__content-treasure">
						<div
							v-if="questCompleted"
							:class="`offer-resource-icon--${contentData.attributes?.skin}--full-360`"
						/>
						<div v-else class="progresable-treasure" />
					</div>

					<div
						v-if="offerActivityQuest"
						class="piggy-bank-offer-detail__content-progress"
					>
						<AppProgressBar :progress="progress" />

						<div v-if="questCompleted" class="rewards-grid__label">
							<AppPrice
								:amount="+offerActivityQuest.objective.amount"
								:currency="
									offerActivityQuest?.reward?.content?.[0]
										?.reward_resource as ResourceType
								"
								:fontSize="24"
								:iconSize="24"
							/>
						</div>
						<div v-else class="progress-sub-info">
							<span class="progress-sub-info__current">
								{{ offerActivityQuest.progress }}
							</span>
							<span class="progress-sub-info__separator">/</span>
							<AppPrice
								:amount="+offerActivityQuest.objective.amount"
								:currency="
									offerActivityQuest?.reward?.content?.[0]
										?.reward_resource as ResourceType
								"
								class="progress-sub-info__target"
								:resourceCheck="false"
								:fontSize="24"
								:iconSize="24"
							/>
						</div>
					</div>
				</div>
			</template>
		</OfferContentBlock>
		<BidirectionalTextLabel
			v-if="questCompleted"
			:text="translate('ui.open_now')"
			:fontSize="14"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import AppPrice from "@/components/AppPrice.vue";
import AppProgressBar from "@/components/AppProgressBar.vue";
import BidirectionalTextLabel from "@/components/BidirectionalTextLabel.vue";
import OfferContentBlock from "@/components/shop/blocks/OfferContentBlock.vue";
import OfferHeaderBlock from "@/components/shop/blocks/OfferHeaderBlock.vue";
import OfferRibbonBlock from "@/components/shop/blocks/OfferRibbonBlock.vue";
import Tooltip from "@/components/Tooltip.vue";
import type { ResourceType } from "@/game/entities/config/resources";
import useQuestStore from "@/store/useQuestStore";
import type { QuestActive } from "@/types/quest";
import type { ShopItem } from "@/types/shop";
import { IconTypes, Placements } from "@/types/ui";
import { floorNumber } from "@/utils/number";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";

interface Props {
	contentData: ShopItem;
}

const { contentData } = defineProps<Props>();

const questStore = useQuestStore();
const { getActiveQuests: activeQuests } = storeToRefs(questStore);

const offerActivityQuest = computed((): QuestActive | undefined =>
	activeQuests.value.find(
		(q: QuestActive): boolean =>
			"activity" in contentData.attributes &&
			q.id === contentData.attributes?.activity,
	),
);

const progress = computed((): number => {
	const quest = offerActivityQuest.value;

	if (!quest?.objective?.amount) return 0;

	const ratio = +quest.progress / +quest.objective.amount;
	return ratio >= 1 ? 100 : floorNumber(ratio * 100);
});

const questCompleted = computed((): boolean => progress.value >= 100);
</script>

<style lang="scss" scoped>
.piggy-bank-offer-detail {
	&__content {
		@include flex-col-center;
		position: relative;
		z-index: 1;
		min-height: 20rem;

		.v-popper.v-popper--theme-tooltip {
			position: absolute;
			top: 0.875rem;
			left: 0.5rem;
		}

		&-cta {
			@include flex-row-center;
			padding: 1rem 2.5rem;
			width: 100%;
			color: #f4d278;
			font-weight: 800;
			font-size: 1.125rem;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}

		&-ribbon {
			width: 12.625rem;
			height: 1.875rem;
			@include background(
				url($path-images + "shop/backgrounds/golden-center-ribbon.avif"),
				contain,
				center,
				no-repeat
			);
			@include flex-row-center;
			z-index: 2;
			color: #fff3dd;
			font-weight: 800;
			font-size: 0.875rem;
			line-height: 1;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}

		&-rewards {
			position: relative;
			background-position: center;
			background-size: cover;
			background-repeat: no-repeat;
			width: 20rem;
			height: 16rem;

			&::before {
				position: absolute;
				top: 50%;
				left: 50%;
				width: 120%;
				height: 120%;
				content: "";
				@include background(
					url($path-images + "shop/backgrounds/radial-glow-xl.avif"),
					contain,
					center,
					no-repeat
				);
				transform: translate(-50%, -50%);
				z-index: -1;
				animation: rotateInfinite 20s linear infinite;
				pointer-events: none;
			}
		}

		&-info {
			position: relative;
			background-image: linear-gradient(
				to right,
				rgba(85, 0, 20, 1),
				rgba(85, 0, 20, 0.8),
				rgba(85, 0, 20, 0.7)
			);
			padding: 0.25rem 2.5rem 0.375rem;
			width: auto;
			@include flex-col-center;
			align-items: flex-start;
			mask-image: linear-gradient(
				to right,
				transparent 2%,
				black 20%,
				black 80%,
				transparent 98%
			);

			&::before,
			&::after {
				position: absolute;
				left: 0;
				z-index: 1;
				background: #cf0032;
				width: 100%;
				height: 0.063rem;
				content: "";
			}

			&::before {
				top: 0;
			}

			&::after {
				bottom: 0;
			}

			span {
				color: #f2dcb5;
				font-weight: 800;
				font-size: 0.875rem;
				line-height: 1.5;
				text-align: center;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
		}

		&-progress {
			padding: 0 2.75rem 2rem;
			width: 100%;

			.progress-bar {
				height: 0.5rem;
			}

			.progress-sub-info {
				@include flex-row-center;
				font-weight: 800;
				font-size: 1.5rem;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

				&__current {
					margin: 0 0.2rem;
					color: #ffc466;
				}

				&__separator,
				&__target :deep(.shrink-text) {
					color: #fff3dd;
				}
			}
		}

		&-treasure {
			position: relative;
			@include flex-col-center;
			width: 100%;
			height: 12rem;

			&::before {
				position: absolute;
				top: 50%;
				left: 50%;
				width: 200%;
				height: 200%;
				content: "";
				@include background(
					url($path-images + "shop/backgrounds/radial-glow-xl.avif"),
					contain,
					center,
					no-repeat
				);
				transform: translate(-50%, -50%);
				z-index: -1;
				animation: rotateInfinite 20s linear infinite;
				pointer-events: none;
			}

			.progresable-treasure {
				position: absolute;
				background: #fff;
				width: 24.5rem;
				height: 14.875rem;
				@include background(
					url(#{$path-images} + "shop/backgrounds/templates/piggy-bank-offer-rewards-sprite--h690-progress.avif"),
					auto 100%,
					center,
					no-repeat
				);
			}
		}

		.rewards-grid__label {
			@include flex-row-center;
			position: relative;
			-webkit-mask-image: linear-gradient(
				to right,
				transparent 2%,
				black 20%,
				black 80%,
				transparent 98%
			);
			mask-image: linear-gradient(
				to right,
				transparent 2%,
				black 20%,
				black 80%,
				transparent 98%
			);
			margin-top: 0.5rem;
			border-width: 0.063rem 0 0.063rem 0;
			border-style: solid;
			border-color: #cd912d;
			width: 100%;
			height: 2.125rem;

			:deep(.price .shrink-text) {
				padding-bottom: 0.25rem;
			}

			&::after {
				position: absolute;
				z-index: -1;
				background: rgb(100, 11, 11);
				width: 100%;
				height: 100%;
				content: "";
			}
		}
	}

	.bidirectional-text-label {
		position: absolute;
		bottom: -0.875rem;
		z-index: 2;
		width: auto;
		height: 1.75rem;
	}
}
</style>
